import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { ConflictNotice } from '../ui/ConflictNotice';
import { CONTRACTS_KEY, type PersonData } from './types';
import { sameValues, useUnsavedGuard } from '../ui/unsaved';

const FIELDS = [
  { key: 'address', label: 'Adres zamieszkania', type: 'text' },
  { key: 'pesel', label: 'PESEL', type: 'text', inputMode: 'numeric' },
  { key: 'birthDate', label: 'Data urodzenia', type: 'date' },
  { key: 'bankIban', label: 'Numer rachunku', type: 'text', inputMode: 'numeric' },
  { key: 'taxOffice', label: 'Urząd skarbowy', type: 'text' },
] as const;
type Key = (typeof FIELDS)[number]['key'];

const personDataKey = (personId: string) => [...CONTRACTS_KEY, 'person-data', personId] as const;

/**
 * "Dane do umów" of a person (F44): address, PESEL, birth date, bank account, tax office. Sensitive: only the fields
 * the server sends are shown (the others are hidden from the role) and only changed fields are saved (rule 7a).
 */
export function PersonDataForm({ personId, canEdit }: { personId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: personDataKey(personId), queryFn: () => api<PersonData>(`/api/contracts/person-data/${personId}`) });
  const data = query.data;
  const [values, setValues] = useState<Partial<Record<Key, string>>>({});
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!data) return;
    setValues(Object.fromEntries(FIELDS.filter((f) => f.key in data).map((f) => [f.key, (data[f.key] as string | null | undefined) ?? ''])));
  }, [data]);
  // owner 2026-10-23 21:14: leaving with unsaved changes asks first
  const saveNow = useRef<() => Promise<boolean>>(async () => false);
  const loaded = data ? Object.fromEntries(FIELDS.filter((f) => f.key in data).map((f) => [f.key, (data[f.key] as string | null | undefined) ?? ''])) : {};
  const guardRef = useUnsavedGuard({ dirty: canEdit && !!data && Object.keys(values).length > 0 && !sameValues(values, loaded), save: () => saveNow.current() });
  if (query.isLoading) return <p className="muted">Wczytywanie danych do umów…</p>;
  if (!data) return null;
  const shown = FIELDS.filter((f) => f.key in data);
  if (shown.length === 0) return <p className="muted">Dane do umów są ukryte dla Twojej roli.</p>;

  /** Saves; false when it did not (the reason is shown). */
  async function persist(): Promise<boolean> {
    setError(null);
    setSaved(false);
    const changed = Object.fromEntries(shown.filter((f) => (values[f.key] ?? '') !== ((data![f.key] as string | null | undefined) ?? '')).map((f) => [f.key, f.key === 'birthDate' && !values[f.key] ? null : values[f.key]]));
    if (Object.keys(changed).length === 0) return true;
    try {
      await api(`/api/contracts/person-data/${personId}`, { method: 'PUT', body: { ...changed, version: data!.version } });
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: CONTRACTS_KEY });
      return true;
    } catch (err) {
      setError(err);
      return false;
    }
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    void persist();
  }
  saveNow.current = persist;
  const issue = (path: string) => (error instanceof ApiError ? fieldIssues(error).find((i) => i.path === path)?.message : undefined);
  return (
    <form ref={guardRef} className="ctr-person-data" onSubmit={submit} aria-label="Dane do umów" data-feature-id="contracts.person_data">
      <h3>Dane do umów</h3>
      {error instanceof ApiError && error.status === 409 && (
        <ConflictNotice
          onReload={() => {
            setError(null);
            void query.refetch();
          }}
        />
      )}
      {error instanceof ApiError && error.status !== 409 && fieldIssues(error).length === 0 && (
        <p className="alert" role="alert">
          {error.message}
        </p>
      )}
      <div className="ctr-grid">
        {shown.map((f) => (
          <label key={f.key} className={f.key === 'address' ? 'field ctr-wide' : 'field'}>
            <span>{f.label}</span>
            <input
              type={f.type}
              inputMode={'inputMode' in f ? f.inputMode : undefined}
              data-no-select-all=""
              value={values[f.key] ?? ''}
              readOnly={!canEdit}
              autoComplete="off"
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
            />
            {issue(f.key) && <small className="field-error">{issue(f.key)}</small>}
          </label>
        ))}
      </div>
      {canEdit && (
        <div className="actions">
          <button className="btn" type="submit">
            Zapisz dane do umów
          </button>
          {saved && (
            <span className="muted" role="status">
              Zapisano
            </span>
          )}
        </div>
      )}
    </form>
  );
}
